import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Users, CalendarDays, Radio, UserCircle, GraduationCap } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePoll } from '../../contexts/PollContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import NextEventPanel from '../../events/components/NextEventPanel';
import LiveEventsPanel from './LiveEventsPanel';
import SquadsQuickPanel from '../../squads/components/SquadsQuickPanel';
import TournamentsQuickPanel from '../../tournaments/components/TournamentsQuickPanel';
import MeQuickPanel from './MeQuickPanel';
import AcademiesQuickPanel from '../../academy/components/AcademiesQuickPanel';

// Items with a `path` navigate directly; items without one open a quick-nav panel instead
// of dropping the user on a flat list — see `panelId` for which panel each opens.
const NAV_ITEMS = [
  { id: 'tournaments', icon: Trophy,       label: 'Tourneys',    path: null,        panelId: 'tournaments' },
  { id: 'squads',      icon: Users,        label: 'Squads',      path: null,        panelId: 'squads' },
  // Labelled singular purely to fit: six tabs give ~59px each on a 375px
  // phone, and "Academies" needs 67px there and would ellipse.
  { id: 'academies',   icon: GraduationCap, label: 'Academy',    path: null,         panelId: 'academies' },
  { id: 'next-event',  icon: CalendarDays, label: 'Events',      path: null,        panelId: 'next-event' },
  { id: 'live',        icon: Radio,        label: 'Live',        path: null,        panelId: 'live' },
  { id: 'me',          icon: UserCircle,   label: 'Me',          path: null,        panelId: 'me' },
];

const MobileNavFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { liveEvents } = usePoll();
  // Same global rollout toggle the header honours — the tab must not appear
  // where the module is switched off.
  const { academiesModule } = useFeatureFlags();
  const navItems = NAV_ITEMS.filter(i => i.id !== 'academies' || academiesModule);
  const [openPanel, setOpenPanel] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Sync selection with the current route when navigating via browser back/forward
  useEffect(() => {
    if (openPanel) return;
    const matched = navItems.find(item => item.path && location.pathname.startsWith(item.path));
    setSelectedId(matched?.id || null);
  }, [location.pathname, openPanel]);

  if (!isAuthenticated) return null;

  const handleClick = (item) => {
    if (item.panelId) {
      const opening = openPanel !== item.panelId;
      setOpenPanel(opening ? item.panelId : null);
      setSelectedId(opening ? item.id : null);
    } else {
      setOpenPanel(null);
      setSelectedId(item.id);
      navigate(item.path);
    }
  };

  const closePanel = () => {
    setOpenPanel(null);
    // Restore selection to current route
    const matched = navItems.find(item => item.path && location.pathname.startsWith(item.path));
    setSelectedId(matched?.id || null);
  };

  return (
    <>
      <NextEventPanel isOpen={openPanel === 'next-event'} onClose={closePanel} />
      <LiveEventsPanel isOpen={openPanel === 'live'} onClose={closePanel} />
      <SquadsQuickPanel isOpen={openPanel === 'squads'} onClose={closePanel} />
      <TournamentsQuickPanel isOpen={openPanel === 'tournaments'} onClose={closePanel} />
      <MeQuickPanel isOpen={openPanel === 'me'} onClose={closePanel} />
      <AcademiesQuickPanel isOpen={openPanel === 'academies'} onClose={closePanel} />

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50">
        <div className={`${theme.card} border-t ${theme.border} px-2 pt-2`} style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
          <div className="flex justify-around items-center">
            {navItems.map((item) => {
              const isActive = selectedId === item.id;
              const Icon = item.icon;
              const isLiveWithEvents = item.id === 'live' && liveEvents.length > 0 && !isActive;

              return (
                <button
                  key={item.id}
                  onClick={() => handleClick(item)}
                  className={`relative flex flex-col items-center justify-center py-2 px-1.5 min-w-0 flex-1 transition-all duration-200 ${
                    isActive
                      ? theme.text
                      : isLiveWithEvents
                        ? 'text-red-500'
                        : `${theme.textMuted} hover:${theme.text}`
                  }`}
                >
                  {/* The selected pill wraps the icon rather than the whole
                      cell. Filling the cell made the active tab read as wider
                      than its neighbours even though every tab is the same
                      width, because it was the only one with a visible box. */}
                  <span className={`relative flex items-center justify-center w-10 h-7 mb-1 rounded-full transition-colors ${
                    isActive ? theme.tabActive : ''
                  }`}>
                    <Icon className={`w-6 h-6 ${isLiveWithEvents ? 'animate-pulse' : ''}`} />
                  </span>
                  <span className="text-[11px] font-medium leading-tight truncate max-w-full">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNavFooter;
